import type { MemoryKindValue } from "@lafayette/schemas";

import { SelectField, TextField } from "../../ui/fields";
import { codeOptions } from "../labels";
import { allowsText, type ValueDraft } from "../value-form";

const WRITE_OWN = "__own_text__";

/**
 * The value of a fact. A code kind is a select of the closed vocabulary, nothing else. The two kinds that
 * allow a short text add one choice, "Write my own text", that shows a text box. The server checks the
 * value again; this control only keeps a staff member inside the lists the contract allows.
 */
export function ValueField({
  idPrefix,
  kind,
  draft,
  onChange,
  error,
}: Readonly<{
  idPrefix: string;
  kind: MemoryKindValue;
  draft: ValueDraft;
  onChange: (draft: ValueDraft) => void;
  error?: string | undefined;
}>) {
  const textAllowed = allowsText(kind);
  const options = [
    ...codeOptions(kind),
    ...(textAllowed ? [{ value: WRITE_OWN, label: "Write my own text…" }] : []),
  ];
  const selected = draft.mode === "text" && textAllowed ? WRITE_OWN : draft.code;

  return (
    <>
      <SelectField
        id={`${idPrefix}-value`}
        label="Value"
        value={selected}
        onChange={(next) =>
          next === WRITE_OWN
            ? onChange({ ...draft, mode: "text" })
            : onChange({ ...draft, mode: "code", code: next })
        }
        options={options}
        placeholder="Choose a value"
        required
        error={draft.mode === "text" ? undefined : error}
      />
      {draft.mode === "text" && textAllowed ? (
        <TextField
          id={`${idPrefix}-text`}
          label="Short text"
          value={draft.text}
          onChange={(text) => onChange({ ...draft, text })}
          maxLength={160}
          required
          error={error}
          hint="3 to 160 characters. Write what the customer said, in their meaning."
        />
      ) : null}
    </>
  );
}
